import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Mail, UserCheck, Paperclip, X, Send, Users, UserPlus, UserMinus, ShieldAlert } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Btn, Tag, Field, Input, Textarea, EmptyState, DeadlineBadge, Select, ModalShell } from "@/components/kit";
import { PhotoGallery } from "@/components/PhotoGallery";
import { useAsync } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { loadBooking, loadTimeline, loadAttachments, loadEmailEvents, applyStatus, loadWriteContext, canWriteBooking, canSelfAssign } from "@/lib/staffData";
import { REQUEST_LABEL, fmtDate, fmtDateTime, INTERNAL_TO_PUBLIC, deadlineInfo } from "@/lib/vda";
import { api } from "@/lib/api";

// best-effort driver email; never blocks the status update if backend/migration unavailable
async function tryNotify(payload) {
  try { return await api.staffNotify(payload); } catch { return null; }
}

const QUICK = [
  { label: "Garage contacted", s: "garage_contacted" },
  { label: "Awaiting garage", s: "awaiting_garage_response" },
  { label: "Vehicle with garage", s: "vehicle_arrived" },
  { label: "Assessing", s: "awaiting_diagnosis" },
  { label: "Awaiting authority", s: "awaiting_internal_authority" },
  { label: "Work approved", s: "authority_granted" },
  { label: "Awaiting parts", s: "parts_ordered" },
  { label: "Work in progress", s: "work_in_progress" },
  { label: "Ready for collection", s: "vehicle_complete" },
  { label: "Complete booking", s: "completed" },
];

export default function StaffBookingDetail({ id: idProp, embedded = false, onClose, onChanged }) {
  const params = useParams();
  const id = idProp || params.id;
  const nav = useNavigate();
  const { session, profile } = useAuth();
  const uid = session?.user?.id;
  const { data, loading, reload } = useAsync(async () => ({
    booking: await loadBooking(id), timeline: await loadTimeline(id).catch(() => []),
    attachments: await loadAttachments(id).catch(() => []), emails: await loadEmailEvents(id).catch(() => []),
    writeCtx: await loadWriteContext(uid).catch(() => null),
  }), [id, uid]);

  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [nextDue, setNextDue] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [showAppt, setShowAppt] = useState(false);
  const [apptDate, setApptDate] = useState("");
  const [garage, setGarage] = useState("");
  const [notifyDriver, setNotifyDriver] = useState(true);

  // internal_status -> auto driver notification type (only meaningful driver-visible changes)
  const AUTO_NOTIFY = { appointment_confirmed: "appointment_confirmed", vehicle_complete: "ready_for_collection", completed: "booking_completed" };

  if (loading) return embedded ? <div className="p-6"><Spinner /></div> : <StaffShell><Spinner /></StaffShell>;
  const b = data.booking;
  if (!b) return embedded ? <div className="p-6"><EmptyState title="Booking not found" /></div> : <StaffShell><EmptyState title="Booking not found" /></StaffShell>;
  const writable = canWriteBooking(b, data.writeCtx);
  const selfAssignable = !writable && canSelfAssign(b, data.writeCtx);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); onChanged?.(); }
    catch (e) {
      const m = (e?.message || "").toLowerCase();
      if (m.includes("row-level security") || m.includes("violates row-level") || e?.code === "42501")
        toast.error("You don't have permission to act on this booking. Join as cover from the panel below, then try again.");
      else toast.error(e.message || "Action failed");
    }
    finally { setBusy(false); }
  };

  const copyText = (text, label) =>
    Promise.resolve(navigator.clipboard?.writeText(text)).then(
      () => toast.success(`${label} copied.`),
      () => toast.error("Couldn't copy — please select and copy manually."),
    );

  const quick = (s) => run(async () => {
    await applyStatus(b, { internal_status: s, internal_note: `Status set to ${s.replace(/_/g, " ")} by ${profile.full_name}.`, uid,
      next_update_due_at: nextDue ? new Date(nextDue).toISOString() : undefined });
    if (AUTO_NOTIFY[s]) await tryNotify({ booking_id: b.id, notification_type: AUTO_NOTIFY[s], notify: true });
  }, "Status updated.");

  const assignToMe = () => run(async () => {
    await api.assignBooking(b.id);
  }, "Assigned to you.");

  const addUpdate = () => {
    if (!note && !msg && !nextDue) { toast.error("Add an internal note, a driver update, or a next-update date."); return; }
    run(async () => {
      await applyStatus(b, { internal_note: note || null, public_message: msg || null, uid,
        next_update_due_at: nextDue ? new Date(nextDue).toISOString() : undefined });
      if (notifyDriver && msg) await tryNotify({ booking_id: b.id, notification_type: "vehicle_update", notify: true, public_message: msg });
      setNote(""); setMsg(""); setNextDue("");
    }, "Update recorded.");
  };

  const chase = () => run(async () => {
    await applyStatus(b, { internal_note: `Garage chased by ${profile.full_name}.`, uid,
      next_update_due_at: nextDue ? new Date(nextDue).toISOString() : new Date(Date.now() + 24 * 3.6e6).toISOString() });
  }, "Garage chase logged.");

  const confirmAppt = () => run(async () => {
    await supabase.from("booking_requests").update({
      appointment_at: apptDate ? new Date(apptDate).toISOString() : null,
      garage_name: garage || b.garage_name,
    }).eq("id", b.id);
    await applyStatus(b, { internal_status: "appointment_confirmed", internal_note: `Appointment confirmed for ${apptDate}.`, uid });
    await tryNotify({ booking_id: b.id, notification_type: "appointment_confirmed", notify: true });
    setShowAppt(false);
  }, "Appointment confirmed.");

  const markSubmitted = () => run(async () => {
    await api.bookingMarkSubmitted(b.id, b.external_booking_reference || null);
  }, "Marked as submitted to the external booking system.");

  const dropoff = () => run(async () => {
    await supabase.from("booking_requests").update({
      dropped_off_at: new Date().toISOString(),
      next_update_due_at: new Date(Date.now() + 24 * 3.6e6).toISOString(),
    }).eq("id", b.id);
    await applyStatus(b, { internal_status: "vehicle_arrived", internal_note: "Vehicle drop-off confirmed. 24h update deadline set.",
      public_message: "Your vehicle is now with the garage.", uid });
  }, "Drop-off recorded, 24h deadline set.");

  const emailSubject = `[${b.human_reference}] ${(REQUEST_LABEL[b.request_type] || b.request_type)} Request | ${b.vehicles?.registration} | ${b.customers?.name}`;
  const emailBody = `Hello,\n\nPlease arrange the following for our fleet vehicle:\n\nReference: ${b.human_reference}\nVehicle: ${[b.vehicles?.registration, [b.vehicles?.make, b.vehicles?.model].filter(Boolean).join(" ")].filter(Boolean).join(" — ")}\nCustomer: ${b.customers?.name} (${b.customers?.customer_code})\nRequest: ${REQUEST_LABEL[b.request_type] || b.request_type}\nMileage: ${b.mileage || "n/a"}\nPreferred dates: ${[b.preferred_date_1, b.preferred_date_2, b.preferred_date_3].filter(Boolean).map(fmtDate).join(", ") || "flexible"}\nCollection required: ${b.collection_required ? "Yes" : "No"}\nCourtesy vehicle: ${b.courtesy_vehicle_required ? "Yes" : "No"}\n\nNotes: ${b.description || "—"}\n\nKind regards,\nVenson Account Team`;

  const sendEmail = () => run(async () => {
    await supabase.from("email_events").insert({
      booking_request_id: b.id, event_type: "garage_email_sent",
      recipient_mailbox: b.customers?.shared_mailbox || "garage@example.com",
      subject: emailSubject, body_preview: emailBody.slice(0, 240), status: "logged",
    });
    await applyStatus(b, { internal_status: "garage_contacted", internal_note: "Garage email prepared and marked as sent.",
      public_message: "We're arranging your booking with the garage.", uid });
    setShowEmail(false);
  }, "Email logged (simulated) & status advanced.");

  const openAttachment = async (path) => {
    const { data: signed } = await supabase.storage.from("booking-attachments").createSignedUrl(path, 3600);
    if (signed?.signedUrl) window.open(signed.signedUrl, "_blank");
    else toast.error("Could not open attachment.");
  };

  const body = (
    <>
      <div className={embedded ? "grid grid-cols-1 gap-4" : "grid lg:grid-cols-3 gap-5"}>
        {/* left: details */}
        <div className={embedded ? "space-y-4" : "lg:col-span-2 space-y-5"}>
          {/* Next action — surfaces the booking's existing queue signals; CTAs trigger existing permitted actions */}
          {b.internal_status !== "completed" && (writable || selfAssignable) && (
            <Panel className="p-4 border-l-[3px] border-l-primary" data-testid="next-action">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <span className="vda-tick text-[hsl(var(--vda-quiet))]">Next action</span>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <DeadlineBadge info={deadlineInfo(b.next_update_due_at)} testId="next-action-deadline" />
                    <Tag tone="green">{b.public_status}</Tag>
                    {!b.assignee?.full_name && <Tag tone="amber">Unassigned</Tag>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!b.assignee?.full_name && <Btn data-testid="next-action-assign" onClick={assignToMe} disabled={busy}><UserCheck className="h-4 w-4" /> Assign to me</Btn>}
                  {writable && <Btn variant={b.assignee?.full_name ? "primary" : "outline"} data-testid="next-action-update" onClick={() => document.getElementById("bd-add-update")?.scrollIntoView({ behavior: "smooth", block: "center" })}>Record an update</Btn>}
                </div>
              </div>
            </Panel>
          )}
          <Panel className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="vda-reg text-xs text-muted-foreground">{b.human_reference}</div>
                <h1 className="text-xl font-semibold text-foreground mt-0.5">{REQUEST_LABEL[b.request_type] || b.request_type}</h1>
              </div>
              <div className="text-right space-y-1">
                <Tag tone="muted">Internal: {b.internal_status.replace(/_/g, " ")}</Tag>
                <div><Tag tone="green">{b.public_status}</Tag></div>
              </div>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Info l="Driver" v={b.drivers?.full_name} />
              <Info l="Driver mobile" v={b.drivers?.mobile_number} />
              <div>
                <div className="vda-tick text-muted-foreground">Vehicle</div>
                <div className="mt-0.5">
                  {b.vehicle_id ? (
                    <button data-testid="bd-vehicle-link" onClick={() => nav(`/staff/vehicle/${b.vehicle_id}`)}
                      className="text-primary hover:underline text-left">
                      {[b.vehicles?.registration, [b.vehicles?.make, b.vehicles?.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "View vehicle record"}
                    </button>
                  ) : "—"}
                </div>
              </div>
              <Info l="Customer" v={`${b.customers?.name} (${b.customers?.customer_code})`} />
              <Info l="Pod" v={b.customers?.pods?.name} />
              <Info l="Assigned AE" v={b.assignee?.full_name || "Unassigned"} />
              <Info l="Appointment" v={fmtDateTime(b.appointment_at)} />
              <Info l="Expected completion" v={fmtDate(b.expected_completion_at)} />
              <Info l="Garage" v={b.garage_name} />
              <Info l="Preferred dates" v={[b.preferred_date_1, b.preferred_date_2, b.preferred_date_3].filter(Boolean).map(fmtDate).join(", ")} />
              <Info l="Mileage" v={b.mileage} />
              <Info l="Next update due" v={fmtDateTime(b.next_update_due_at)} />
            </div>
            {b.description && <div className="mt-3 text-sm"><span className="vda-tick text-muted-foreground">Request details</span><p className="mt-1">{b.description}</p></div>}
            {b.request_details?.accident_report_id && (
              <div className="mt-3">
                <Btn variant="outline" data-testid="bd-accident-link" onClick={() => nav(`/staff/accident/${b.request_details.accident_report_id}`)}>
                  <ShieldAlert className="h-4 w-4" /> View accident report {b.request_details.accident_reference || ""}
                </Btn>
              </div>
            )}
          </Panel>

          {/* attachments & evidence */}
          <Panel className="p-5">
            <h2 className="font-semibold flex items-center gap-2"><Paperclip className="h-4 w-4" /> Attachments &amp; evidence</h2>
            <PhotoGallery attachments={data.attachments} testId="bd-gallery" />
          </Panel>

          {/* timeline — operational ledger */}
          <Panel className="p-0 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border"><h2 className="font-semibold">Timeline</h2></div>
            {data.timeline.length === 0 ? (
              <div className="p-5"><EmptyState title="No timeline entries yet" hint="Updates appear here as the booking progresses." /></div>
            ) : (
              <div>
                {data.timeline.map((u, i) => (
                  <div key={u.id} data-testid="timeline-item" className="grid grid-cols-[104px_1fr] gap-4 px-5 py-3 border-b border-border last:border-b-0">
                    <div className="font-mono text-[11px] leading-relaxed text-[hsl(var(--vda-quiet))]">{fmtDateTime(u.created_at)}</div>
                    <div className="relative pl-4">
                      <span className="absolute left-0 top-[7px] vda-marker" style={{ backgroundColor: i === 0 ? "hsl(var(--vda-green))" : "hsl(var(--vda-strong-border))" }} aria-hidden="true" />
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.public_status && <span className={`text-sm font-semibold ${i === 0 ? "text-foreground" : "text-muted-foreground"}`}>{u.public_status}</span>}
                        {u.source === "bulk" && <Tag tone="clay">bulk</Tag>}
                        {u.source === "import" && <Tag tone="clay">import</Tag>}
                        {u.author?.full_name && <span className="text-xs text-muted-foreground">· {u.author.full_name}</span>}
                      </div>
                      {u.internal_note && <p className="text-sm mt-1 text-muted-foreground"><span className="vda-tick text-[hsl(var(--vda-clay))] mr-1">Internal</span>{u.internal_note}</p>}
                      {u.public_message && <p className="text-sm mt-1"><span className="vda-tick text-primary mr-1">Driver-visible</span>{u.public_message}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* email events */}
          <Panel className="p-5">
            <h2 className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4" /> Email events (simulated)</h2>
            {data.emails.length === 0 ? <p className="text-sm text-muted-foreground mt-2">No emails logged.</p> : (
              <div className="mt-3 space-y-2">
                {data.emails.map((e) => (
                  <div key={e.id} className="text-sm border border-border rounded-[6px] p-3">
                    <div className="vda-reg text-xs">{e.subject}</div>
                    <div className="text-xs text-muted-foreground mt-1">To {e.recipient_mailbox} · {fmtDateTime(e.created_at)} · {e.status}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* right: actions */}
        <div className="space-y-4">
          {!writable && (
            <Panel className="p-4 border-l-[3px] border-l-[hsl(var(--vda-amber))] bg-[hsl(var(--vda-amber-bg))]" data-testid="readonly-banner">
              <h2 className="font-semibold text-sm">Read-only for you</h2>
              <p className="text-xs text-foreground/80 mt-1">
                {selfAssignable
                  ? <>This request is in {b.customers?.pods?.name || "your pod"} and unassigned. Assign it to yourself to take ownership and unlock the operational actions.</>
                  : <>This request is owned by {b.customers?.pods?.name || "another pod"} and isn't assigned to you. You can view everything, but operational actions are limited to its pod, assignee or a cover colleague. Join as cover below if you need to help.</>}
              </p>
              {selfAssignable && <Btn variant="clay" className="w-full mt-2" data-testid="assign-me" onClick={assignToMe} disabled={busy}><UserCheck className="h-4 w-4" /> Assign to me</Btn>}
            </Panel>
          )}
          {b.request_category === "service_mot" && writable && !["completed", "cancelled"].includes(b.internal_status) && (
            <Panel className="p-4 space-y-3 border-l-[3px] border-l-primary" data-testid="raise-external">
              <div>
                <h2 className="font-semibold text-sm">Raise external booking</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Book this in 1link/Epyx, then mark it submitted. Nothing is sent from here.</p>
              </div>
              <div className="border border-border rounded-[6px] divide-y divide-border text-sm">
                {[
                  ["Registration", b.vehicles?.registration],
                  ["VDA reference", b.human_reference],
                  ["Work needed", REQUEST_LABEL[b.request_type] || b.request_type],
                  ["Customer", b.customers?.name],
                  ["Mileage", b.mileage ? String(b.mileage) : null],
                ].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <div className="vda-tick text-muted-foreground">{l}</div>
                      <div className="truncate">{v || "—"}</div>
                    </div>
                    {v && (
                      <button data-testid={`copy-${l.toLowerCase().replace(/ /g, "-")}`}
                        onClick={() => copyText(v, l)}
                        className="p-1.5 text-muted-foreground hover:text-foreground flex-none" title={`Copy ${l}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {b.description && (
                <button data-testid="copy-details" onClick={() => copyText(b.description, "Request details")}
                  className="w-full text-left text-xs border border-border rounded-[6px] px-3 py-2 hover:bg-secondary">
                  <span className="vda-tick text-muted-foreground flex items-center gap-1"><Copy className="h-3 w-3" /> Copy request details</span>
                  <span className="block mt-1 line-clamp-2 text-muted-foreground">{b.description}</span>
                </button>
              )}
              {b.external_booking_reference && <div className="text-xs"><span className="vda-tick text-muted-foreground">External ref</span> <span className="vda-reg">{b.external_booking_reference}</span></div>}
              <Btn variant="clay" className="w-full" data-testid="mark-submitted" onClick={markSubmitted} disabled={busy}>Mark booking submitted</Btn>
              {b.submitted_at && <p className="text-xs text-primary">Submitted {fmtDateTime(b.submitted_at)}. Awaiting confirmation email → intake in Confirmations.</p>}
            </Panel>
          )}
          <Panel className="p-4 space-y-2">
            <h2 className="font-semibold text-sm">Actions</h2>
            {writable ? (<>
            <Btn variant="outline" className="w-full" data-testid="assign-me" onClick={assignToMe} disabled={busy}><UserCheck className="h-4 w-4" /> Assign to me</Btn>
            <Btn variant="clay" className="w-full" data-testid="prepare-email" onClick={() => setShowEmail(true)} disabled={busy}><Mail className="h-4 w-4" /> Prepare garage email</Btn>
            <Btn variant="outline" className="w-full" data-testid="confirm-appt" onClick={() => { setApptDate(""); setGarage(b.garage_name || ""); setShowAppt(true); }} disabled={busy}>Confirm appointment</Btn>
            <Btn variant="outline" className="w-full" data-testid="confirm-dropoff-staff" onClick={dropoff} disabled={busy}>Confirm dropped off</Btn>
            <Btn variant="outline" className="w-full" data-testid="chase-garage" onClick={chase} disabled={busy}>Mark garage chased</Btn>
            </>) : (selfAssignable
              ? <Btn variant="clay" className="w-full" data-testid="assign-me-actions" onClick={assignToMe} disabled={busy}><UserCheck className="h-4 w-4" /> Assign to me</Btn>
              : <p className="text-xs text-muted-foreground">Operational actions are read-only for you on this request.</p>)}
          </Panel>

          {!["completed", "cancelled"].includes(b.internal_status) && (
            <CoverPanel bookingId={b.id} uid={uid} role={profile?.role} onChanged={() => { reload(); onChanged?.(); }} />
          )}

          {writable && b.pod_id && !["completed", "cancelled"].includes(b.internal_status) && (
            <AssignPanel bookingId={b.id} podId={b.pod_id} currentAssigneeId={b.assigned_staff_user_id} onChanged={() => { reload(); onChanged?.(); }} />
          )}

          <Panel className="p-4">
            <h2 className="font-semibold text-sm mb-2">Set status</h2>
            {writable ? (
            <div className="grid grid-cols-2 gap-2">
              {QUICK.map((a) => (
                <button key={a.s} data-testid={`status-${a.s}`} onClick={() => quick(a.s)} disabled={busy}
                  className="text-xs border border-border rounded-[6px] px-2 py-2 hover:bg-secondary text-left disabled:opacity-50">
                  {a.label}
                  <span className="block text-[10px] text-muted-foreground">{INTERNAL_TO_PUBLIC[a.s]}</span>
                </button>
              ))}
            </div>
            ) : <p className="text-xs text-muted-foreground">Status changes are limited to this request's pod, assignee or cover.</p>}
          </Panel>

          {writable && (
          <Panel className="p-4 space-y-3" id="bd-add-update">
            <h2 className="font-semibold text-sm">Add update</h2>
            <Field label="Internal note (never shown to driver)">
              <Textarea data-testid="internal-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private note for the team" className="border-[hsl(var(--vda-clay))]/40" />
            </Field>
            <Field label="Driver-visible update">
              <Textarea data-testid="public-message" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message the driver will see" className="border-primary/40" />
            </Field>
            <Field label="Next update due">
              <Input data-testid="next-update-due" type="datetime-local" value={nextDue} onChange={(e) => setNextDue(e.target.value)} />
            </Field>
            <label className="flex items-center gap-2 text-sm" data-testid="notify-driver-toggle">
              <input type="checkbox" checked={notifyDriver} onChange={(e) => setNotifyDriver(e.target.checked)} />
              Notify driver by email (when a driver-visible update is added)
            </label>
            <Btn className="w-full" data-testid="add-update" onClick={addUpdate} disabled={busy}>Record update</Btn>
          </Panel>
          )}
        </div>
      </div>

      {/* garage email modal */}
      {showEmail && (
        <Modal onClose={() => setShowEmail(false)} title="Prepared garage email">
          <div className="space-y-3">
            <div>
              <span className="vda-tick text-muted-foreground">Subject</span>
              <div className="vda-reg text-sm border border-border rounded-[6px] p-2 mt-1">{emailSubject}</div>
            </div>
            <div>
              <span className="vda-tick text-muted-foreground">Body</span>
              <pre className="text-xs whitespace-pre-wrap border border-border rounded-[6px] p-3 mt-1 bg-secondary/40 font-mono">{emailBody}</pre>
            </div>
            <div className="flex gap-2">
              <Btn variant="outline" onClick={() => copyText(`${emailSubject}\n\n${emailBody}`, "Email")}><Copy className="h-4 w-4" /> Copy</Btn>
              <Btn variant="clay" data-testid="mark-email-sent" onClick={sendEmail} disabled={busy}><Send className="h-4 w-4" /> Mark as sent</Btn>
            </div>
            <p className="text-xs text-muted-foreground">No live email is sent in this phase. Marking as sent logs an email event and advances the booking.</p>
          </div>
        </Modal>
      )}

      {showAppt && (
        <Modal onClose={() => setShowAppt(false)} title="Confirm appointment">
          <div className="space-y-3">
            <Field label="Appointment date & time"><Input data-testid="appt-date" type="datetime-local" value={apptDate} onChange={(e) => setApptDate(e.target.value)} /></Field>
            <Field label="Garage"><Input data-testid="appt-garage" value={garage} onChange={(e) => setGarage(e.target.value)} placeholder="Garage name" /></Field>
            <Btn className="w-full" data-testid="save-appt" onClick={confirmAppt} disabled={busy || !apptDate}>Confirm appointment</Btn>
          </div>
        </Modal>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="h-full" data-testid="booking-pane">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
          <div>
            <span className="vda-tick text-[hsl(var(--vda-quiet))]">Selected · {b.human_reference}</span>
            <div className="vda-reg text-base font-bold tracking-wide mt-0.5">{b.vehicles?.registration}</div>
          </div>
          <button onClick={onClose} data-testid="close-pane" className="p-1.5 text-muted-foreground hover:text-foreground" aria-label="Close booking preview"><X className="h-4 w-4" /></button>
        </div>
        {body}
      </div>
    );
  }
  return (
    <StaffShell>
      <button onClick={() => nav("/staff")} className="text-sm text-muted-foreground mb-4 hover:text-foreground">← Back to queues</button>
      {body}
    </StaffShell>
  );
}

function CoverPanel({ bookingId, uid, role, onChanged }) {
  const { data, loading, reload } = useAsync(() => api.coverHistory(bookingId), [bookingId]);
  const staff = useAsync(async () => {
    const { data } = await supabase.from("user_profiles")
      .select("id, full_name, role, active")
      .in("role", ["account_executive", "senior_account_executive", "account_manager", "pod_lead", "administrator"])
      .eq("active", true).order("full_name");
    return data || [];
  }, []);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("");
  const [joinReason, setJoinReason] = useState("");
  const canAssign = ["pod_lead", "account_manager", "administrator"].includes(role);
  const history = data?.history || [];
  const active = history.filter((h) => h.active);
  const mine = active.find((h) => h.staff_user_id === uid);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); onChanged?.(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <Panel className="p-4 space-y-3" data-testid="cover-panel">
      <h2 className="font-semibold text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Cover</h2>
      <p className="text-xs text-muted-foreground">Cover lets colleagues from other pods help with this booking without changing its owning pod or primary AE.</p>

      {loading ? <Spinner /> : (
        <>
          <div data-testid="cover-active-list">
            {active.length === 0 ? (
              <p className="text-xs text-muted-foreground">No active cover.</p>
            ) : (
              <div className="space-y-1.5">
                {active.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 border border-border rounded-[6px] px-2.5 py-1.5 text-sm" data-testid={`cover-active-${c.id}`}>
                    <span className="truncate">{c.staff?.full_name || "Staff"}{c.staff_user_id === uid && <span className="text-muted-foreground text-xs"> · you</span>}</span>
                    <button data-testid={`cover-end-${c.id}`} onClick={() => act(() => api.coverEnd(c.id), "Cover ended.")} disabled={busy}
                      className="text-xs text-[hsl(var(--vda-red))] hover:underline inline-flex items-center gap-1"><UserMinus className="h-3.5 w-3.5" /> End</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {mine ? (
            <Btn variant="outline" className="w-full" data-testid="cover-leave" onClick={() => act(() => api.coverEnd(mine.id), "You left cover.")} disabled={busy}>
              <UserMinus className="h-4 w-4" /> Leave cover
            </Btn>
          ) : (
            <div className="space-y-2">
              <Input data-testid="cover-join-reason" value={joinReason} onChange={(e) => setJoinReason(e.target.value)}
                placeholder="Reason (optional) — why are you covering?" />
              <Btn variant="clay" className="w-full" data-testid="cover-join"
                onClick={() => act(() => api.coverJoin(bookingId, joinReason.trim() || null), "Joined as cover — it's now in My Work.")} disabled={busy}>
                <UserPlus className="h-4 w-4" /> Join as cover
              </Btn>
            </div>
          )}

          {canAssign && (
            <div className="pt-2 border-t border-border space-y-2">
              <span className="vda-tick text-muted-foreground">Assign cover</span>
              <Select data-testid="cover-assign-select" value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="">Select a colleague…</option>
                {(staff.data || []).filter((s) => !active.some((a) => a.staff_user_id === s.id)).map((s) => (
                  <option key={s.id} value={s.id}>{s.full_name}</option>
                ))}
              </Select>
              <Btn variant="outline" className="w-full" data-testid="cover-assign-btn" disabled={busy || !target}
                onClick={() => act(async () => { await api.coverAssign(bookingId, target, "Assigned cover"); setTarget(""); }, "Cover assigned.")}>
                <UserPlus className="h-4 w-4" /> Assign cover
              </Btn>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

function AssignPanel({ bookingId, podId, currentAssigneeId, onChanged }) {
  const { data } = useAsync(() => (podId ? api.podAssignees(podId) : Promise.resolve({ assignees: [] })), [podId]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const options = (data?.assignees || []).filter((s) => s.id !== currentAssigneeId);
  const assign = async () => {
    if (!target) return;
    setBusy(true);
    try { await api.assignBooking(bookingId, target); toast.success("Assigned to colleague."); setTarget(""); onChanged?.(); }
    catch (e) { toast.error(e.message || "Could not assign."); }
    finally { setBusy(false); }
  };
  return (
    <Panel className="p-4 space-y-2" data-testid="assign-ae-panel">
      <h2 className="font-semibold text-sm flex items-center gap-2"><UserCheck className="h-4 w-4" /> Assign to another AE</h2>
      <p className="text-xs text-muted-foreground">Hand this booking to a colleague in its owning pod. They become the individual assignee.</p>
      <Select data-testid="assign-ae-select" value={target} onChange={(e) => setTarget(e.target.value)}>
        <option value="">Select a colleague…</option>
        {options.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
      </Select>
      <Btn variant="outline" className="w-full" data-testid="assign-ae-btn" disabled={busy || !target} onClick={assign}>
        <UserCheck className="h-4 w-4" /> Assign to selected AE
      </Btn>
    </Panel>
  );
}

function Info({ l, v }) {
  return (
    <div>
      <div className="vda-tick text-muted-foreground">{l}</div>
      <div className="mt-0.5">{v || "—"}</div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <ModalShell onClose={onClose} className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">{title}</h3>
        <button onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
      {children}
    </ModalShell>
  );
}
